import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SessionSummaryDto, AuthenticatedUser } from '@replayops/contracts';
import { api } from './client.js';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get<AuthenticatedUser>('/v1/auth/me')).data,
  });
}

export function useProjects(orgId?: string) {
  return useQuery({
    enabled: !!orgId,
    queryKey: ['projects', orgId],
    queryFn: async () => (await api.get(`/v1/orgs/${orgId}/projects`)).data.data as ProjectDto[],
  });
}

export interface ProjectDto {
  id: string;
  name: string;
  publicKey: string;
  environment: string;
  retentionDays: number;
  samplingRate: number;
  samplingMode: 'user' | 'session';
  userRuleMode: 'off' | 'allow' | 'deny';
  ipRuleMode: 'off' | 'allow' | 'deny';
}

export function useCreateProject(orgId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; environment?: string }) =>
      (await api.post(`/v1/orgs/${orgId}/projects`, input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', orgId] }),
  });
}

export function useSessions(orgId?: string, projectId?: string) {
  return useQuery({
    enabled: !!orgId && !!projectId,
    queryKey: ['sessions', orgId, projectId],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/projects/${projectId}/sessions`)).data
        .data as SessionSummaryDto[],
  });
}

export function useSearchSessions(orgId?: string, projectId?: string) {
  return useMutation({
    mutationFn: async (dto: Record<string, unknown>) =>
      (await api.post(`/v1/orgs/${orgId}/projects/${projectId}/search`, dto)).data as {
        data: SessionSummaryDto[];
        total: number;
      },
  });
}

export function useSessionEvents(orgId?: string, projectId?: string, sessionId?: string) {
  return useQuery({
    enabled: !!orgId && !!projectId && !!sessionId,
    queryKey: ['events', sessionId],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/projects/${projectId}/sessions/${sessionId}/events`)).data,
  });
}

/** Org-wide sessions (all projects, or one) with the owning project's name. */
export interface OrgSessionQuery {
  projectId?: string;
  excludeBots?: boolean;
  trafficSource?: string;
  browser?: string;
  os?: string;
  country?: string;
  minDurationMs?: number;
  maxDurationMs?: number;
  minInteractions?: number;
  sortBy?: 'startedAt' | 'durationMs' | 'interactionCount' | 'frustrationScore' | 'browser' | 'os';
  sortDir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export function useOrgSessions(orgId?: string, q: OrgSessionQuery = {}) {
  return useQuery({
    enabled: !!orgId,
    queryKey: ['org-sessions', orgId, q],
    queryFn: async () => {
      const p = new URLSearchParams();
      p.set('limit', String(q.limit ?? 50));
      p.set('offset', String(q.offset ?? 0));
      if (q.projectId) p.set('projectId', q.projectId);
      if (q.excludeBots) p.set('excludeBots', 'true');
      if (q.trafficSource) p.set('trafficSource', q.trafficSource);
      if (q.browser) p.set('browser', q.browser);
      if (q.os) p.set('os', q.os);
      if (q.country) p.set('country', q.country);
      if (q.minDurationMs != null) p.set('minDurationMs', String(q.minDurationMs));
      if (q.maxDurationMs != null) p.set('maxDurationMs', String(q.maxDurationMs));
      if (q.minInteractions != null) p.set('minInteractions', String(q.minInteractions));
      if (q.sortBy) p.set('sortBy', q.sortBy);
      if (q.sortDir) p.set('sortDir', q.sortDir);
      const res = (await api.get(`/v1/orgs/${orgId}/sessions?${p.toString()}`)).data as {
        data: SessionSummaryDto[];
        pagination?: { total: number };
      };
      return { data: res.data, total: res.pagination?.total ?? res.data.length };
    },
  });
}

/** Session metadata (visitor device/geo/ip, counts) for the replay details panel. */
export function useSession(orgId?: string, projectId?: string, sessionId?: string) {
  return useQuery({
    enabled: !!orgId && !!projectId && !!sessionId,
    queryKey: ['session', sessionId],
    queryFn: async () =>
      (await api.get<SessionSummaryDto>(`/v1/orgs/${orgId}/projects/${projectId}/sessions/${sessionId}`)).data,
  });
}

export function useAnalyticsOverview(orgId?: string, projectId?: string, days = 30) {
  return useQuery({
    enabled: !!orgId && !!projectId,
    queryKey: ['overview', orgId, projectId, days],
    queryFn: async () => {
      const to = new Date().toISOString();
      const from = new Date(Date.now() - days * 86_400_000).toISOString();
      return (
        await api.get(
          `/v1/orgs/${orgId}/projects/${projectId}/analytics/overview?from=${from}&to=${to}`,
        )
      ).data;
    },
  });
}
